import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ParsedParam } from "@/lib/mcp/schema";
import { useInspectorStore } from "@/stores/inspectorStore";
import { Play, RotateCcw } from "lucide-react";
import { ParameterInput } from "../shared/components";
import { useParameterManagement } from "../shared/hooks/useParameterManagement";

export interface ToolParameterFormProps {
  toolName: string | null;
  parsedParams: ParsedParam[];
  onExecute: () => void;
  isExecuting: boolean;
  canExecute: boolean;
  showParametersChanged?: boolean;
}

export function ToolParameterForm({
  toolName,
  parsedParams,
  onExecute,
  isExecuting,
  canExecute,
  showParametersChanged = false,
}: ToolParameterFormProps) {
  const {
    getToolState,
    updateToolParameters,
    updateToolParamErrors,
    setToolResponse,
    invalidateConfigureSection,
  } = useInspectorStore();

  const { paramValues, paramErrors, validateAndSetParameter, defaultValues } =
    useParameterManagement({
      type: "tool",
      selectedName: toolName,
      parsedParams,
      getState: getToolState,
      updateParameters: updateToolParameters,
      updateErrors: updateToolParamErrors,
      setResponse: setToolResponse,
      invalidateConfigureSection,
    });

  const hasErrors = Object.values(paramErrors).some((error) => error);

  const resetToDefaults = () => {
    if (!toolName) {
      return;
    }

    updateToolParameters(toolName, defaultValues);
    updateToolParamErrors(toolName, {});
  };

  if (!toolName) {
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canExecute && !hasErrors) {
      onExecute();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Parameters Changed Indicator */}
      {showParametersChanged && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg font-medium">Parameters</span>
          </div>
          <Badge variant="destructive" className="text-xs">
            Parameters changed - re-execute needed
          </Badge>
        </div>
      )}

      {/* Parameters Section */}
      <div>
        {parsedParams.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg text-center">
            This tool takes no parameters.
          </div>
        ) : (
          <div className="space-y-4">
            {parsedParams.map((param) => (
              <ParameterInput
                key={param.name}
                parameter={param}
                value={paramValues[param.name] ?? ""}
                error={paramErrors[param.name]}
                onChange={(value) =>
                  validateAndSetParameter(param.name, value, param)
                }
                idPrefix="tool-param"
              />
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={resetToDefaults}
          disabled={isExecuting}
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!canExecute || hasErrors || isExecuting}
          className="flex-1"
        >
          <Play className="w-4 h-4 mr-2" />
          {isExecuting ? "Executing..." : "Execute Tool"}
        </Button>
      </div>
    </form>
  );
}
